import type { FeatureCollection } from "@turf/helpers";
import type { Map } from "mapbox-gl";
import type React from "react";
import { useEffect, useState } from "react";
import { delay, parseGeoData } from "senf-shared";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import DrawingUiElements from "./DrawingUiElements";
import { useIcons } from "./hooks/useIcons";
import { useLabels } from "./hooks/useLabels";
import DrawMapbox from "./utils/drawing/DrawMapbox";
import {
  handleContinueDrawingFromLastVertex,
  handleSetDrawType,
  handleTriggerDrawingFromLastVertex,
  safeDrawingOperation,
  undoCreateDrawing,
} from "./utils/drawing/drawingFunctions";

interface MapType {
  mode: "draw";
  drawType: "point" | "path" | "area" | "mapbounds";
}

export interface MapboxDrawingFeatureProps {
  mapType: MapType;
  statefulMap: Map;
  triggerRefresh: boolean;
  setFeatureId: (featureId: string | null) => void;
  featureId: string | null;
  bottom: number | string;
  featureCollection: FeatureCollection | string;
  handleSaveDrawn: (featureCollection: FeatureCollection) => void;
  setOpenDescription: (open: boolean) => void;
  openDescription: boolean;
  maxFeatures?: number;
  inMainMap?: boolean;
  isMobile?: boolean;
}

const MapboxDrawingFeature: React.FC<MapboxDrawingFeatureProps> = ({
  mapType,
  statefulMap,
  shouldTriggerRefresh,
  setFeatureId,
  featureId,
  bottom,
  featureCollection,
  handleSaveDrawn,
  openDescription,
  maxFeatures = 10000,
  inMainMap,
  deleteFeature,
  setDrawMode,
  initialDrawType,
  allowClickMapToStartDrawing = true,
  descriptionProvided,
  setOpenDescriptionProp,
  ...props
}) => {
  const setOpenDescription =
    setOpenDescriptionProp || (() => console.log("drawing feature"));

  const [setLabelsLayer, setLabelsData] = useLabels("drawLabels", featureId);
  const [setIconsLayer, setIconsData] = useIcons("drawIcons", featureId);
  // const [updateDrawingLabels, initializeDrawingLabels, resetDrawingLabelsData] =
  //   useAddDrawingLabels(statefulMap, true);
  const [currentlyDrawing, setCurrentlyDrawing] = useState(false);
  const [continueFeat, setContinueFeat] = useState(false);
  const isMobileFallback = useIsMobile();
  const isMobile = props.isMobile ?? isMobileFallback;
  const [triggerRefresh, setTriggerRefresh] = useState(false);

  const [showMessage, setShowMessage] = useState(false);

  // useEffect(() => {
  //   if (featureId) {
  //     const { drawType } = (
  //       featureCollection as FeatureCollection
  //     )?.features?.find((feature) => feature.id === featureId)?.properties || {
  //       drawType: null,
  //     };
  //     if (drawType) setDrawMode({ mode: "draw", drawType });
  //   }
  // }, [featureId]);

  useEffect(() => {
    if (initialDrawType === "path") {
      setDrawMode({ mode: null, drawType: null });
      safeDrawingOperation(statefulMap, (df) => df.deleteAll());
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "path" });
      }, 50);
    }
    if (initialDrawType === "area") {
      setDrawMode({ mode: null, drawType: null });
      safeDrawingOperation(statefulMap, (df) => df.deleteAll());
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "area" });
      }, 50);
    } else if (initialDrawType === "point") {
      setDrawMode({ mode: null, drawType: null });
      safeDrawingOperation(statefulMap, (df) => df.deleteAll());
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "point" });
      }, 50);
    } else if (initialDrawType === "mapbounds") {
      setDrawMode({ mode: null, drawType: null });
      safeDrawingOperation(statefulMap, (df) => df.deleteAll());
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "mapbounds" });
      }, 50);
    } else {
      setDrawMode({ mode: "draw", drawType: null });
      safeDrawingOperation(statefulMap, (df) => df.deleteAll());
    }
  }, [statefulMap?.df, initialDrawType]);

  useEffect(() => {
    setTimeout(() => {
      setTriggerRefresh(true);
      // if (flyToGeoData) {
      //   flyToPolygon(statefulMap, flyToGeoData, {
      //     duration: 1000,
      //     pitch: 0,
      //     bearing: 0,
      //     padding: { top: 50, bottom: 50, left: 50, right: 50 },
      //   });
      // }

      setTimeout(() => {
        setTriggerRefresh(false);
      }, 10);
    }, 10);
  }, [shouldTriggerRefresh]);

  useEffect(() => {
    if (featureId) {
      const inputElement = document.getElementById(
        `surveyQuestionInput-${featureId}`,
      );
      if (inputElement && !isMobile) {
        inputElement.click();
      }
    }
  }, [featureId, currentlyDrawing]);

  const handleSetAddDescription = (featureId, type) => {
    if (type === "save") {
      setFeatureId(null);
      setOpenDescription(false);
      statefulMap?.df.changeMode("simple_select");
    } else if (type === "onCreateLastDot" || type === "onCreate") {
      if (isMobile) {
        setOpenDescription(true);
      }
      if (isMobile && !setOpenDescriptionProp) {
        setFeatureId(null);
      }
    } else if (type === "onFeatureClick") {
      if (isMobile) {
        setOpenDescription(true);
      }
    } else {
      setOpenDescription(featureId);
    }
  };

  const handleSaveAll = (featureId: string) => {
    if (featureId) {
      // IF NOT DELETE

      if (mapType?.drawType === "point") {
        statefulMap?.df?.setFeatureProperty(
          featureId,
          "drawType",
          mapType?.drawType,
        );
      }
    }
    if (
      (mapType.drawType === "area" ||
        mapType.drawType === "path" ||
        mapType.drawType === "mapbounds") &&
      featureId
    ) {
      statefulMap?.df.changeMode("simple_select");
      setTimeout(() => {
        if (featureId) {
          statefulMap?.df.changeMode("direct_select", { featureId });
        }
      }, 100);
    }

    handleSaveDrawn(statefulMap.df.getAll());
    setCurrentlyDrawing(false);
  };

  const handleDelete = (featureId: string) => {
    deleteFeature?.(featureId);

    statefulMap?.df.delete(featureId);
    setFeatureId(null);
    setOpenDescription(false);

    handleSaveDrawn(statefulMap.df.getAll());
    setCurrentlyDrawing(false);

    setTimeout(() => {
      statefulMap?.df.changeMode("simple_select");
    }, 120);

    // handleSaveAll();
  };

  /**
   * Initialize drawing feature on load
   */
  useEffect(() => {
    if (statefulMap && mapType?.mode === "draw") {
      if (!statefulMap.df) statefulMap.addControl(DrawMapbox);
      statefulMap.df = DrawMapbox;
      setLabelsLayer(statefulMap);
      setIconsLayer(statefulMap);
      setOpenDescription(false);
    }
  }, [statefulMap, mapType.mode, triggerRefresh]);

  /**
   * Clean up drawing in main-map after closing post idea || After changing page in survey
   */

  useEffect(() => {
    return () => {
      if (inMainMap || triggerRefresh) {
        setFeatureId(null);
        safeDrawingOperation(statefulMap, (df) => {
          df.deleteAll();
          df.changeMode("simple_select");
        });
        setLabelsData(null);
        setIconsData(null);
      }
    };
  }, [inMainMap, triggerRefresh]);

  /**
   * Show drawn data
   */
  useEffect(() => {
    if (statefulMap?.df && featureCollection) {
      setLabelsData(featureCollection);
      setIconsData(featureCollection);
      statefulMap.df.add(parseGeoData(featureCollection));
    }
  }, [statefulMap?.df, featureCollection, triggerRefresh]);

  /**
   * necessary to set the drawType if not using to click the map to set the drawType
   */
  useEffect(() => {
    if (
      mapType.drawType &&
      statefulMap?._loaded &&
      !allowClickMapToStartDrawing
    ) {
      handleSetDrawType(statefulMap.df, mapType);
    }
  }, [statefulMap?.df, statefulMap?._loaded, mapType]);

  useEffect(() => {
    let onClick;
    let onCreate;
    let onLineTap;
    let onFeatureClick;
    let onVertexClick;

    if (statefulMap?.df && mapType?.mode === "draw") {
      onFeatureClick = async (event) => {
        const { e, state, featureId } = event;
        console.log(featureId, e, statefulMap?.df, mapType?.mode);

        setCurrentlyDrawing(true);
        setFeatureId(featureId);

        handleSetAddDescription(featureId, "onFeatureClick");
      };

      onClick = async (e) => {
        // HANDLE NOTHING CLICKED

        if (
          !e.featureTarget &&
          (statefulMap?.df.getMode() === "simple_select" ||
            statefulMap?.df.getMode() === "direct_select") &&
          allowClickMapToStartDrawing &&
          openDescription === false &&
          featureCollection?.features?.length >= maxFeatures
        ) {
          setShowMessage((prev) => {
            if (!prev) {
              setTimeout(() => setShowMessage(false), 3000);
            }
            return true;
          });
          return;
        }
        if (
          !featureCollection ||
          featureCollection?.features?.length < maxFeatures
        ) {
          await delay(10);
          if (
            !e.featureTarget &&
            (statefulMap?.df.getMode() === "simple_select" ||
              statefulMap?.df.getMode() === "direct_select") &&
            allowClickMapToStartDrawing &&
            openDescription === false
          ) {
            console.log("NOTHING CLICKED", featureCollection);

            handleSetDrawType(statefulMap.df, mapType);
          }
        }
      };

      onCreate = (e) => {
        // HANDLE DRAWING NEW POINTS
        if (mapType.drawType === "point") {
          const featureId = e.features[0].id;

          setTimeout(() => {
            handleSaveAll(featureId);
          }, 100);
          handleSetAddDescription(featureId, "onCreate");
          setOpenDescription(true);
          setFeatureId(isMobile ? featureId : null);

          console.log("Handle draw new points");
        } else {
          // HANDLE FINISHING PATHS & AREAS BY CLICKING THE LAST DOT TWICE
          setTimeout(() => {
            handleSaveAll(featureId);
            setFeatureId(isMobile ? featureId : null);
            handleSetAddDescription(featureId, "onCreateLastDot");
          }, 100);
        }
      };

      onLineTap = (event) => {
        // HANDLE START DRAWING NEW PATHS AND AREAS
        setCurrentlyDrawing(true);
        const { e, state, featureId } = event;
        if (featureId) {
          statefulMap?.df?.setFeatureProperty(
            featureId,
            "drawType",
            mapType?.drawType,
          );
          setFeatureId(featureId);
          handleSetAddDescription(featureId, "onLinetap");
        }
        if (undoCreateDrawing(e, statefulMap.df, featureId, mapType)) {
          setFeatureId(null);
        }
      };

      onVertexClick = (event) => {
        const featureId = event.e.featureTarget.properties.parent;
        setCurrentlyDrawing(true);
        setFeatureId(featureId);
        handleSetAddDescription(featureId, "onVertexClick");

        handleTriggerDrawingFromLastVertex(
          event.e,
          statefulMap.df,
          featureId,
          setContinueFeat,
        );
      };

      statefulMap.on("draw.onVertexClick", onVertexClick);
      statefulMap.on("draw.onFeatureClick", onFeatureClick);
      statefulMap.on("draw.onLineTap", onLineTap);
      statefulMap.on("draw.create", onCreate);
      statefulMap.on("mousedown", onClick);
      statefulMap.on("touchstart", onClick);

      return () => {
        statefulMap.off("draw.onVertexClick", onVertexClick);
        statefulMap.off("draw.onFeatureClick", onFeatureClick);
        statefulMap.off("draw.onLineTap", onLineTap);
        statefulMap.off("draw.create", onCreate);
        statefulMap.off("mousedown", onClick);
        statefulMap.off("touchstart", onClick);
      };
    }
  }, [
    statefulMap,
    statefulMap?.df,
    triggerRefresh,
    currentlyDrawing,
    mapType,
    openDescription,
    featureCollection,
    maxFeatures,
  ]);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === " ") {
        if (
          allowClickMapToStartDrawing &&
          openDescription === false &&
          featureCollection?.features?.length >= maxFeatures
        ) {
          setShowMessage((prev) => {
            if (!prev) {
              setTimeout(() => setShowMessage(false), 3000);
            }
            return true;
          });
          statefulMap?.df?.changeMode("simple_select");
          return;
        }
      }
    };

    const mapElement = document.getElementsByClassName("mapboxgl-canvas")[0];

    if (mapElement && mapType.drawType) {
      document.addEventListener("keydown", handleKeydown);

      return () => {
        if (mapElement) {
          document.removeEventListener("keydown", handleKeydown);
        }
      };
    }
  }, [
    statefulMap,
    featureCollection,
    maxFeatures,
    allowClickMapToStartDrawing,
    openDescription,
  ]);

  useEffect(() => {
    let lastInteractionWasKeyboard = false;

    const handleKeydown = (event) => {
      if (event.key === "Tab") {
        lastInteractionWasKeyboard = true;
      }
    };

    const handleMousedown = () => {
      lastInteractionWasKeyboard = false;
    };

    const handleFocus = (event) => {
      if (lastInteractionWasKeyboard) {
        const currentMode = statefulMap.df?.getMode();
        const drawTypeToModeMap = {
          area: "custom_draw_polygon",
          path: "custom_draw_line_string",
          point: "custom_draw_point",
        };
        const targetMode = drawTypeToModeMap[mapType.drawType];
        console.log(currentMode, targetMode);
        if (currentMode !== targetMode) {
          handleSetDrawType(statefulMap.df, mapType);
        }
      }
    };

    const handleBlur = () => {
      console.log("REMOVE");
    };

    const mapElement = document.getElementsByClassName("mapboxgl-canvas")[0];

    if (mapElement && mapType.drawType) {
      document.addEventListener("keydown", handleKeydown);
      document.addEventListener("mousedown", handleMousedown);
      mapElement.addEventListener("focus", handleFocus);
      mapElement.addEventListener("blur", handleBlur);
    }

    return () => {
      if (mapElement) {
        document.removeEventListener("keydown", handleKeydown);
        document.removeEventListener("mousedown", handleMousedown);
        mapElement.removeEventListener("focus", handleFocus);
        mapElement.removeEventListener("blur", handleBlur);
      }
    };
  }, [statefulMap?._loaded, mapType]);

  return (
    mapType?.mode === "draw" && (
      <DrawingUiElements
        mapType={mapType}
        statefulMap={statefulMap}
        bottom={bottom}
        featureId={featureId}
        handleDelete={handleDelete}
        handleSaveAll={handleSaveAll}
        handleContinueDrawingFromLastVertex={
          handleContinueDrawingFromLastVertex
        }
        handleSetAddDescription={handleSetAddDescription}
        // alreadyDrawn={alreadyDrawn}
        continueFeat={continueFeat}
        setContinueFeat={setContinueFeat}
        setOpenDescription={setOpenDescription}
        openDescription={openDescription}
        descriptionProvided={descriptionProvided}
        featureCollection={featureCollection}
        showMessage={showMessage}
        isMobile={isMobile}
        // setDrawContinuing={setDrawContinuing}
      />
    )
  );
};

export default MapboxDrawingFeature;
